// Sponsor / CMO classification of productMaterialsList rows.
//
// The Sponsor and CMO material tables are both rendered from the same
// productMaterialsList. Each row is marked with `_isSponsorLinked`:
//   true  -> sponsor material (shown in the Sponsor table AND the CMO table)
//   false -> CMO-only mapping (shown in the CMO table only)
//
// fetchProductDetails no longer returns sponsorSupplyingMaterial (null / absent);
// the product-level productDetails.subContractingPOs replaced it. Rows that still
// carry a boolean sponsorSupplyingMaterial keep the original rule. Rows without it
// are classified from their own data:
//   - CMO-only rows are always saved with an empty material_brand / bo_material_UOM,
//     while sponsor rows require Brand, Material Type and UOM.
//   - So, per bo_material_code, the sponsor material is the first row that has a
//     Material Type and sponsor-side data (Brand or UOM); if no row has sponsor-side
//     data, the first row with a Material Type is used (same as the UI rule for new rows).
//   - Every other row with that bo_material_code is a CMO-only mapping.

const hasText = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

export const hasSponsorSupplyingFlag = (material) =>
  typeof material?.sponsorSupplyingMaterial === "boolean";

/**
 * Returns a boolean per row (same order as `materials`) telling whether the row is
 * the sponsor material. Order of `materials` does not affect which row is chosen
 * when sponsor-side data is present.
 */
export const getSponsorLinkedFlags = (materials = []) => {
  const flags = materials.map(() => false);

  // 1) Rows with a boolean sponsorSupplyingMaterial - original rule, unchanged
  const sponsorCodesFromFlag = new Set();
  materials.forEach((material, index) => {
    if (!hasSponsorSupplyingFlag(material)) return;
    flags[index] =
      material.sponsorSupplyingMaterial === true && hasText(material.materialTypeName);
    if (flags[index] && hasText(material.bo_material_code)) {
      sponsorCodesFromFlag.add(String(material.bo_material_code).trim());
    }
  });

  // 2) Rows without the flag - one sponsor row per bo_material_code
  const preferredIndexByCode = new Map();
  const fallbackIndexByCode = new Map();
  materials.forEach((material, index) => {
    if (hasSponsorSupplyingFlag(material)) return;
    if (material?.is_material_active === 1) return; // deleted rows are never displayed
    if (!hasText(material?.bo_material_code) || !hasText(material?.materialTypeName)) return;
    const code = String(material.bo_material_code).trim();
    if (sponsorCodesFromFlag.has(code)) return;

    const hasSponsorData = hasText(material.material_brand) || hasText(material.bo_material_UOM);
    if (hasSponsorData && !preferredIndexByCode.has(code)) {
      preferredIndexByCode.set(code, index);
    }
    if (!fallbackIndexByCode.has(code)) {
      fallbackIndexByCode.set(code, index);
    }
  });

  fallbackIndexByCode.forEach((fallbackIndex, code) => {
    const index = preferredIndexByCode.has(code) ? preferredIndexByCode.get(code) : fallbackIndex;
    flags[index] = true;
  });

  return flags;
};

/**
 * sponsorSupplyingMaterial is deprecated: it may be used locally (rows created in
 * the UI still carry it) but must never be sent to the API. Returns a copy of the
 * request payload with the field removed from materialDetails.productMaterialsList;
 * everything else is passed through unchanged.
 */
export const omitSponsorSupplyingMaterial = (payload) => {
  const list = payload?.materialDetails?.productMaterialsList;
  if (!Array.isArray(list)) return payload;
  return {
    ...payload,
    materialDetails: {
      ...payload.materialDetails,
      productMaterialsList: list.map((material) => {
        if (!material || typeof material !== "object") return material;
        const { sponsorSupplyingMaterial, ...rest } = material;
        return rest;
      }),
    },
  };
};

/**
 * When the product-level subContractingPOs is explicitly false, allocation
 * configurations are not applicable: allocationConfiguration is never sent (even
 * when it was copied from a replicated product) and updateAllocationConfigFlag is
 * always sent as false. Otherwise the payload is returned unchanged.
 */
export const omitAllocationWhenNotApplicable = (payload, subContractingPOs) => {
  if (subContractingPOs !== false || !payload) {
    return payload;
  }
  const { allocationConfiguration, ...rest } = payload;
  return { ...rest, updateAllocationConfigFlag: false };
};

/**
 * Sponsor/CMO side of a single row for keys and comparisons: the boolean
 * sponsorSupplyingMaterial when present (original behaviour), otherwise the
 * derived `_isSponsorLinked` marker.
 */
export const isSponsorMaterial = (material) =>
  hasSponsorSupplyingFlag(material)
    ? material.sponsorSupplyingMaterial
    : !!material?._isSponsorLinked;
